import { useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  ClipboardCheck,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  PlusCircle,
  UserCog,
  UserRound,
  X,
} from "lucide-react";
import { canManageUsers, type Role } from "@academichub/shared";
import { useAuth } from "../hooks/useAuth";
import { isValidationQueueVisible } from "../lib/activity";
import { roleLabel } from "../lib/format";
import { activeNavTarget } from "../lib/nav";
import { Button, cn } from "./ui";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Bila diisi, menu hanya tampil untuk peran yang lolos pemeriksaan ini. */
  visibleFor?: (role: Role | undefined | null) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dasbor", icon: <LayoutDashboard className="size-5" aria-hidden /> },
  { to: "/kegiatan", label: "Kegiatan", icon: <FileText className="size-5" aria-hidden /> },
  {
    to: "/kegiatan/baru",
    label: "Catat Kegiatan",
    icon: <PlusCircle className="size-5" aria-hidden />,
  },
  {
    to: "/validasi",
    label: "Antrean Validasi",
    icon: <ClipboardCheck className="size-5" aria-hidden />,
    visibleFor: isValidationQueueVisible,
  },
  { to: "/laporan", label: "Laporan", icon: <BarChart3 className="size-5" aria-hidden /> },
  {
    to: "/pengguna",
    label: "Kelola Pengguna",
    icon: <UserCog className="size-5" aria-hidden />,
    visibleFor: canManageUsers,
  },
  { to: "/profil", label: "Profil", icon: <UserRound className="size-5" aria-hidden /> },
];

/** Kerangka halaman terautentikasi: sidebar desktop + laci navigasi mobile. */
export function RoleLayout() {
  const { user, logoutNow } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = NAV_ITEMS.filter((item) => !item.visibleFor || item.visibleFor(user?.role));
  // Hanya SATU menu yang menyala: tujuan paling spesifik yang cocok dengan URL.
  const activeTarget = activeNavTarget(
    location.pathname,
    items.map((item) => item.to),
  );

  async function handleLogout(): Promise<void> {
    try {
      await logoutNow();
    } finally {
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }

  const nav = (
    <nav className="flex flex-col gap-1">
      {items.map((item) => {
        const isActive = item.to === activeTarget;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isActive ? "page" : undefined}
            onClick={() => setMenuOpen(false)}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition",
              isActive
                ? "bg-brand-600 text-white"
                : "text-slate-600 hover:bg-slate-200/70 hover:text-slate-900",
            )}
          >
            {item.icon}
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-dvh bg-slate-100 lg:flex">
      <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-slate-200 bg-white p-4 lg:flex">
        <div className="space-y-6">
          <Link to="/" className="block">
            <span className="text-base font-bold text-brand-700">ISTN Activity Hub</span>
            <span className="mt-1 block text-xs text-slate-500">
              Institut Sains dan Teknologi Nasional
            </span>
          </Link>
          {nav}
        </div>
        <UserBox
          fullName={user?.fullName ?? "-"}
          role={user ? roleLabel(user.role) : "-"}
          onLogout={handleLogout}
        />
      </aside>

      <div className="flex min-h-dvh flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <button
            type="button"
            aria-label="Buka menu"
            onClick={() => setMenuOpen(true)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
          >
            <Menu className="size-5" aria-hidden />
          </button>
          <span className="text-sm font-bold text-brand-700">ISTN Activity Hub</span>
          <span className="text-xs text-slate-500">{user ? roleLabel(user.role) : ""}</span>
        </header>

        {menuOpen ? (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              aria-label="Tutup menu"
              className="absolute inset-0 bg-slate-900/50"
              onClick={() => setMenuOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 flex w-72 flex-col justify-between bg-white p-4 shadow-xl">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-brand-700">Menu</span>
                  <button
                    type="button"
                    aria-label="Tutup"
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                  >
                    <X className="size-5" aria-hidden />
                  </button>
                </div>
                {nav}
              </div>
              <UserBox
                fullName={user?.fullName ?? "-"}
                role={user ? roleLabel(user.role) : "-"}
                onLogout={handleLogout}
              />
            </div>
          </div>
        ) : null}

        <main className="mx-auto w-full max-w-6xl flex-1 space-y-5 px-4 py-5 sm:px-6 sm:py-8">
          <div key={location.pathname} className="space-y-5">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function UserBox({
  fullName,
  role,
  onLogout,
}: {
  fullName: string;
  role: string;
  onLogout: () => void;
}) {
  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
      <div>
        <p className="truncate text-sm font-semibold text-slate-800">{fullName}</p>
        <p className="text-xs text-slate-500">{role}</p>
      </div>
      <Button variant="secondary" className="w-full" onClick={onLogout}>
        <LogOut className="size-4" aria-hidden />
        Keluar
      </Button>
    </div>
  );
}
